import { useTranslation } from 'react-i18next'

import { Button } from '#/shared/ui/button'
import { NoteEditor } from '#/shared/ui/note-editor'
import { VoiceInputButton } from '#/shared/ui/voice-recording-island'

import { ExistingNotes } from './existing-notes'
import type { PanelState } from './panel-state'

function SimpleNoteComposer({
  note,
  shipmentId,
}: {
  note: PanelState['note']
  shipmentId: string | undefined
}) {
  const { t } = useTranslation()
  const canSave = note.value.trim().length > 0 && !note.isSaving
  return (
    <div className="flex flex-col">
      <div className="p-2.5">
        <div className="relative flex max-h-50 min-h-21 resize-y flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-3">
          <textarea
            value={note.value}
            onChange={(e) => note.onChange(e.target.value)}
            placeholder={t('editor.note.placeholder')}
            rows={3}
            className="min-h-0 w-full flex-1 resize-none bg-transparent pr-9 text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
          />
          <VoiceInputButton
            value={note.value}
            onChange={note.onChange}
            disabled={note.isSaving}
            className="absolute top-2 right-2"
          />
        </div>
      </div>
      <ExistingNotes shipmentId={shipmentId} />
      <div className="sticky bottom-0 z-10 flex h-14 items-center justify-end gap-2.5 border-t border-gray-200 bg-gray-100 px-4">
        <Button
          variant="outline"
          size="xs"
          disabled={note.isSaving}
          onClick={note.onCancel}
        >
          {t('editor.note.cancel')}
        </Button>
        <Button
          size="xs"
          disabled={!canSave}
          onClick={() => void note.onSave(note.value)}
        >
          {note.isSaving ? t('table.saving') : t('editor.note.save')}
        </Button>
      </div>
    </div>
  )
}

export function NoteTab({
  note,
  compact,
  shipmentId,
}: {
  note: PanelState['note']
  compact?: boolean
  shipmentId?: string
}) {
  const { t } = useTranslation()
  if (compact) return <SimpleNoteComposer note={note} shipmentId={shipmentId} />
  return (
    <NoteEditor
      value={note.value}
      onChange={note.onChange}
      onCancel={note.onCancel}
      onSave={note.onSave}
      enableVoice
      isSubmitting={note.isSaving}
      saveLabel={note.isSaving ? t('table.saving') : t('editor.note.save')}
    />
  )
}
